import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import PageFooter from "../../components/PageFooter";
import PageHeader from "../../components/PageHeader";
import PlanFeatureUpgradeButton from "../../components/PlanFeatureUpgradeButton";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";
import Toggle from "../../components/Toggle";
import api from "../../services/api";
import { getData, setData } from "../../services/auth";
import { renderError } from "../../helpers/errors";

const PRODUCT_PAGE_FEATURES = [
    {
        key: 'product_mock',
        name: 'Simular com meu logo',
        description: 'O visitante envia o logo e vê o produto com a marca aplicada antes de pedir o orçamento.',
    },
    {
        key: 'whatsapp_quote',
        name: 'Botão "Orçar no WhatsApp"',
        description: 'Abre uma conversa já com o produto e a quantidade. Desligado, o pedido passa pelo carrinho e chega como orçamento no sistema.',
    },
];

const CATALOG_FEATURES = [
    {
        key: 'price_filter',
        name: 'Filtro de preço',
        description: 'Faixa de preço por unidade na listagem de produtos e na busca.',
    },
    {
        key: 'stock_filter',
        name: 'Filtro de estoque',
        description: 'Estoque mínimo na listagem de produtos e na busca, com atalhos de 100 a 5.000 unidades.',
    },
];

const PRICE_DISPLAY_OPTIONS = [
    {
        value: null,
        title: 'Não exibir',
        description: 'O site não mostra preço pra ninguém.',
    },
    {
        value: 'only_approved_can_view',
        title: 'Só para cadastros aprovados',
        description: 'Você libera cada cliente ou empresa no cadastro dele, em "Pode visualizar preço".',
    },
    {
        value: 'all_can_view',
        title: 'Para quem tem cadastro',
        description: 'Basta ter cadastro para ver o preço, não precisa de aprovação.',
    },
    {
        value: 'full_view',
        title: 'Para todos',
        description: 'Qualquer visitante vê o preço, sem precisar entrar.',
    },
];

const sameConfig = (a, b) => a.price_display_option === b.price_display_option
    && Object.keys(a.features).every(key => a.features[key] === b.features[key]);

export const SiteFeatures = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(null);
    const [features, setFeatures] = useState({});
    const [priceDisplayOption, setPriceDisplayOption] = useState(null);
    const [available, setAvailable] = useState({});
    const [whatsappConfigured, setWhatsappConfigured] = useState(true);
    const [upgradeFeature, setUpgradeFeature] = useState(null);

    useEffect(() => {
        getConfig();
    }, []);

    const applyResource = resource => {
        setFeatures(resource.features ?? {});
        setPriceDisplayOption(resource.price_display_option ?? null);
        setAvailable(resource.available ?? {});
        setWhatsappConfigured(resource.whatsapp_configured !== false);
        setSaved(resource);
    }

    const getConfig = () => {
        setLoading(true);

        api.get(`/site-features`).then(res => {
            applyResource(res.data?.resource ?? {});
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const dirty = useMemo(() => {
        if (!saved) return false;
        return !sameConfig(saved, { features, price_display_option: priceDisplayOption });
    }, [saved, features, priceDisplayOption]);

    const save = () => {
        setSaving(true);

        api.put(`/site-features`, { features, price_display_option: priceDisplayOption }).then(res => {
            const resource = res.data?.resource ?? {};
            applyResource(resource);

            // Cadastro de cliente e de empresa leem o modo da sessão para
            // mostrar o "Pode visualizar preço"; sem isso só valeria no próximo login.
            setData({ ...(getData() ?? {}), price_display_option: resource.price_display_option ?? null });

            toast.success('Recursos atualizados no site');
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    }

    const discard = () => {
        if (!saved) return;
        applyResource(saved);
    }

    const renderToggleRow = feature => {
        const locked = available[feature.key] === false;
        const checked = !locked && features[feature.key] === true;

        return (
            <div key={feature.key} className={`site-feature-row ${checked ? '' : 'off'}`}>
                <div className="site-feature-text">
                    <span className="site-feature-name">
                        {feature.name}
                        {locked && <PlanFeatureUpgradeButton onClick={() => setUpgradeFeature(feature.key)} />}
                    </span>
                    <span className="site-feature-description">{feature.description}</span>

                    {feature.key === 'whatsapp_quote' && checked && !whatsappConfigured &&
                        <span className="site-feature-hint">
                            <Icon name="alert" size={14} strokeWidth={2} />
                            <span>O botão só aparece com o link do WhatsApp preenchido em <Link to="/contact">Contato e mídias</Link>.</span>
                        </span>
                    }
                </div>

                <Toggle
                    checked={checked}
                    disabled={locked || loading}
                    label={feature.name}
                    onChange={value => setFeatures(prev => ({ ...prev, [feature.key]: value }))}
                />
            </div>
        );
    }

    const priceDisplayLocked = available.price_display === false;

    return (
        <PanelTemplate>
            <PageHeader left={<h1>Recursos do site</h1>} />

            <div className="site-features-page">
                <Box title={`Página do produto`}>
                    {PRODUCT_PAGE_FEATURES.map(renderToggleRow)}
                </Box>

                <Box title={`Listagem e busca`}>
                    {CATALOG_FEATURES.map(renderToggleRow)}
                </Box>

                <Box
                    title={`Exibição de preço`}
                    tools={priceDisplayLocked && <PlanFeatureUpgradeButton onClick={() => setUpgradeFeature('price_display')} />}
                >
                    <div className={`price-display-options ${priceDisplayLocked ? 'locked' : ''}`}>
                        {PRICE_DISPLAY_OPTIONS.map(option => (
                            <button
                                key={option.value ?? 'none'}
                                type="button"
                                className={`price-display-option ${priceDisplayOption === option.value ? 'on' : ''}`}
                                onClick={() => setPriceDisplayOption(option.value)}
                                disabled={priceDisplayLocked || loading}
                                aria-pressed={priceDisplayOption === option.value}
                            >
                                <strong>{option.title}</strong>
                                <span>{option.description}</span>
                            </button>
                        ))}
                    </div>
                </Box>
            </div>

            <PageFooter spaceBetween>
                <span className="site-features-status">
                    {dirty ? 'Alterações não salvas.' : 'Tudo salvo.'}
                </span>
                <div className="site-features-actions">
                    <Button
                        type={`secondary`}
                        size={`small`}
                        text={`Descartar`}
                        action={discard}
                        disabled={!dirty || saving || loading}
                    />
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`save-white`}
                        text={`Salvar alterações`}
                        action={save}
                        loading={saving || loading}
                        disabled={!dirty}
                    />
                </div>
            </PageFooter>

            <PlanFeatureUpgradeModal
                feature={upgradeFeature}
                show={upgradeFeature !== null}
                close={() => setUpgradeFeature(null)}
            />
        </PanelTemplate>
    );
}

export default SiteFeatures;
